import React, { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '@/store';
import { selectOnboardingCompleted } from '@/store/selectors';
import { useAppLock } from './AppLock';
import { Header } from './Header';
import { Dashboard } from './Dashboard/Dashboard';
import { CourseDetailLayout } from './CourseDetail/CourseDetailLayout';
import { SetupWizard } from './SetupWizard/SetupWizard';
import { ExportImportBar } from './ExportImport/ExportImportBar';
import { SettingsPanel } from './SettingsPanel';
import { AlertBanner } from './Alerts/AlertBanner';
import { ToastProvider, ErrorBoundary, PanelBoundary, ReportErrorContext } from './shared';
import { FeedbackWidgetForm, FeedbackButton } from './FeedbackWidget/FeedbackWidget';
import { LoadDemoModal } from './DemoCourse/LoadDemoModal';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import appIdentity from 'virtual:app-identity';
import type { UserFeedback } from '@/utils/userFeedback';

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

export function App(): React.ReactElement {
  const [hydrated, setHydrated] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExportImport, setShowExportImport] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showLoadDemo, setShowLoadDemo] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type: UserFeedback['type'] | undefined;
    category: string | undefined;
    error: string | undefined;
  }>({ type: undefined, category: undefined, error: undefined });

  const selectedCourseId = useAppStore((s) => s.selectedCourseId);
  const onboardingCompleted = useAppStore(selectOnboardingCompleted);
  const hydrate = useAppStore((s) => s.hydrate);
  const { lock, isEnabled, enableEncryption } = useAppLock();

  // Hydrate from IndexedDB on mount
  useEffect(() => {
    hydrate().then(() => setHydrated(true));
  }, []);

  // Show wizard on first visit
  useEffect(() => {
    if (hydrated && !onboardingCompleted) {
      setShowWizard(true);
    }
  }, [hydrated, onboardingCompleted]);

  // Apply theme
  const theme = useAppStore((s) => s.settings.theme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else if (theme === 'light') {
      root.removeAttribute('data-theme');
    } else {
      // System
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => {
        if (e.matches) root.setAttribute('data-theme', 'dark');
        else root.removeAttribute('data-theme');
      };
      if (mq.matches) root.setAttribute('data-theme', 'dark');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme]);

  const handleOpenFeedback = useCallback(
    (
      type: UserFeedback['type'] | undefined,
      category: string | undefined,
      error: string | undefined,
    ) => {
      setFeedbackConfig({ type, category, error });
      setFeedbackOpen(true);
    },
    [],
  );

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({ type: undefined, category: undefined, error: undefined });
  }, []);

  if (!hydrated) {
    return (
      <div className="app-loading" role="status" aria-live="polite" aria-label="Loading Absence Budget">
        <svg className="app-loading__gauge" viewBox="0 0 120 68" aria-hidden="true">
          <path d="M10 62 A50 50 0 0 1 110 62" className="app-loading__gauge-track" fill="none" strokeWidth="10" strokeLinecap="round" />
          <path d="M10 62 A50 50 0 0 1 110 62" className="app-loading__gauge-sweep" fill="none" strokeWidth="10" strokeLinecap="round" />
        </svg>
        <h1 className="app-loading__title">Absence Budget</h1>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ReportErrorContext.Provider value={(msg) => handleOpenFeedback('bug_report', 'error', msg)}>
      <ToastProvider>
        <a href="#main-content" className="skip-to-content">Skip to content</a>
      <Header
        onOpenSettings={() => setShowSettings(true)}
        onOpenExportImport={() => setShowExportImport(true)}
        onStartWizard={() => setShowWizard(true)}
        onOpenLoadDemo={() => setShowLoadDemo(true)}
        lockInstance={lock ?? undefined}
        isLockEnabled={isEnabled}
        onEnableLock={isEnabled ? undefined : enableEncryption}
      />

      <AlertBanner />

      {selectedCourseId ? (
        <PanelBoundary panelName="Course detail">
          <CourseDetailLayout />
        </PanelBoundary>
      ) : (
        <PanelBoundary panelName="Courses dashboard">
          <Dashboard onStartWizard={() => setShowWizard(true)} />
        </PanelBoundary>
      )}

      <PanelBoundary panelName="Setup wizard">
        <SetupWizard
          isOpen={showWizard}
          onComplete={() => setShowWizard(false)}
          onLoadDemo={() => setShowLoadDemo(true)}
        />
      </PanelBoundary>
      <ExportImportBar
        isOpen={showExportImport}
        onClose={() => setShowExportImport(false)}
      />
      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onOpenAbout={() => {
          setShowSettings(false);
          setShowAbout(true);
        }}
      />
      <LoadDemoModal
        isOpen={showLoadDemo}
        onClose={() => setShowLoadDemo(false)}
      />
      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        config={aboutConfig}
      />

      {/* Feedback widget (feedback-widget module) */}
      <FeedbackWidgetForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      <FeedbackButton onOpen={handleOpenFeedback} />
      </ToastProvider>
      </ReportErrorContext.Provider>
    </ErrorBoundary>
  );
}
